import { useState, useEffect, useRef, useCallback } from 'react';
import {
  isBackendAvailable, listClients, createClient as apiCreateClient,
  updateClient as apiUpdateClient, resetClientPassword as apiResetClientPassword,
  deleteClient as apiDeleteClient,
  listPlans, createPlan as apiCreatePlan, updatePlan as apiUpdatePlan,
  forkPlan as apiForkPlan, deletePlan as apiDeletePlan,
} from './lib/api';

/**
 * Plan persistence layer.
 * - Advisors: pick a client, open/create plans in that client's dashboard.
 * - Clients: see their own dashboard. The advisor's plan is the benchmark and
 *   is read-only. The moment a client changes anything on it we offer to fork
 *   it into their own option, rather than letting them edit a plan that will
 *   never save.
 * Autosaves the full app snapshot (debounced) whenever an editable plan is open.
 */
export default function usePlanManager({ user, serializePlan, loadPlanData }) {
  const isClient = user?.role === 'client';
  const [clients, setClients] = useState([]);
  const [activeClientId, setActiveClientId] = useState(null);
  const [plans, setPlans] = useState([]);
  const [activePlan, setActivePlan] = useState(null); // full plan object from API
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'readonly' | 'error'
  const [loading, setLoading] = useState(false);
  const [pendingFork, setPendingFork] = useState(false); // client edited the advisor's plan
  const saveTimer = useRef(null);
  const skipNextAutosave = useRef(false);
  // Snapshot taken the moment a plan finishes loading. Any later difference
  // while the plan is read-only means the client has started editing.
  const openedJson = useRef(null);

  const online = isBackendAvailable() && !!user;
  const canEditPlan = useCallback((plan) =>
    !!plan && (!isClient || plan.created_by_role !== 'advisor'), [isClient]);

  const refreshClients = useCallback(async () => {
    if (!online || isClient) return;
    try {
      const list = await listClients();
      setClients(list);
      if (list.length && activeClientId == null) setActiveClientId(list[0].id);
    } catch { /* backend offline */ }
  }, [online, isClient, activeClientId]);

  const refreshPlans = useCallback(async (clientId) => {
    if (!online) return [];
    const cid = isClient ? null : (clientId ?? activeClientId);
    if (!isClient && cid == null) { setPlans([]); return []; }
    try {
      const list = await listPlans(cid);
      setPlans(list);
      return list;
    } catch { return []; }
  }, [online, isClient, activeClientId]);

  useEffect(() => { refreshClients(); }, [refreshClients]);

  // Switching clients drops whatever plan was open, because it belongs to the
  // client you just left. The first assignment is not a switch, though:
  // activeClientId starts null and becomes the first client only once the list
  // arrives from the server. Clearing on that transition wipes a plan that was
  // opened while the list was still in flight — which is a race the advisor hits
  // by opening a plan quickly, and the PDF renderer hits every time.
  const prevClientId = useRef(activeClientId);
  useEffect(() => {
    const switched = prevClientId.current != null && prevClientId.current !== activeClientId;
    prevClientId.current = activeClientId;
    refreshPlans();
    if (switched) { setActivePlan(null); setSaveStatus(null); }
  }, [activeClientId]);
  useEffect(() => { if (isClient && online) refreshPlans(); }, [isClient, online]);

  const openPlan = useCallback((plan) => {
    skipNextAutosave.current = true; // loading a plan mutates state; don't echo it back
    openedJson.current = null;       // re-baselined by the effect once state settles
    setPendingFork(false);
    loadPlanData(plan.plan_data);
    setActivePlan(plan);
    setSaveStatus(canEditPlan(plan) ? 'saved' : 'readonly');
  }, [loadPlanData, canEditPlan]);

  const saveNewPlan = useCallback(async (name) => {
    if (!online) return null;
    setLoading(true);
    try {
      const plan = await apiCreatePlan(name, serializePlan(), isClient ? null : activeClientId);
      setActivePlan(plan);
      setSaveStatus('saved');
      await refreshPlans();
      return plan;
    } catch (e) { setSaveStatus('error'); return null; }
    finally { setLoading(false); }
  }, [online, serializePlan, isClient, activeClientId, refreshPlans]);

  const forkActivePlan = useCallback(async (name) => {
    if (!online || !activePlan) return null;
    setLoading(true);
    try {
      const fork = await apiForkPlan(activePlan.id, name);
      // Fork carries the source snapshot; user's current edits become the fork's first save
      const updated = await apiUpdatePlan(fork.id, { planData: serializePlan() });
      setActivePlan(updated);
      setSaveStatus('saved');
      await refreshPlans();
      return updated;
    } catch (e) { setSaveStatus('error'); return null; }
    finally { setLoading(false); }
  }, [online, activePlan, serializePlan, refreshPlans]);

  /* Rename without touching the snapshot. Throws so the caller can show the
     server's message — a client renaming the advisor's plan gets a 403. */
  const renamePlan = useCallback(async (planId, name) => {
    const updated = await apiUpdatePlan(planId, { name });
    if (activePlan?.id === planId) setActivePlan(updated);
    await refreshPlans();
    return updated;
  }, [activePlan, refreshPlans]);

  const removePlan = useCallback(async (planId) => {
    if (!online) return;
    await apiDeletePlan(planId); // throws on 403 so the caller can surface it
    if (activePlan?.id === planId) { setActivePlan(null); setSaveStatus(null); }
    await refreshPlans();
  }, [online, activePlan, refreshPlans]);

  const addClient = useCallback(async (username, password, displayName) => {
    const c = await apiCreateClient(username, password, displayName); // throws on failure — caller shows message
    await refreshClients();
    setActiveClientId(c.id);
    return c;
  }, [refreshClients]);

  /* Rename a client or change the username they sign in with. Throws on
     failure so the caller can surface the server's message verbatim. */
  const editClient = useCallback(async (clientId, changes) => {
    const c = await apiUpdateClient(clientId, changes);
    await refreshClients();
    return c;
  }, [refreshClients]);

  const resetClientPassword = useCallback(async (clientId, password) => {
    return apiResetClientPassword(clientId, password);
  }, []);

  /* Removes the client and every plan in their dashboard. */
  const removeClient = useCallback(async (clientId) => {
    const res = await apiDeleteClient(clientId);
    if (activeClientId === clientId) {
      setActiveClientId(null);
      setActivePlan(null);
      setPlans([]);
      setSaveStatus(null);
    }
    await refreshClients();
    return res;
  }, [refreshClients, activeClientId]);

  // ── Debounced autosave of the live snapshot into the open (editable) plan ──
  const snapshot = serializePlan(); // cheap: plain object of current state
  const snapshotJson = JSON.stringify(snapshot);
  useEffect(() => {
    if (!online || !activePlan) return;
    const editable = canEditPlan(activePlan);

    // First snapshot after a plan loads becomes the baseline, not an edit.
    if (skipNextAutosave.current) {
      skipNextAutosave.current = false;
      openedJson.current = snapshotJson;
      setSaveStatus(editable ? 'saved' : 'readonly');
      return;
    }

    if (!editable) {
      // The client changed something on the advisor's plan. Nothing will
      // persist here, so surface the fork now instead of dropping the work.
      if (openedJson.current !== null && snapshotJson !== openedJson.current) {
        setPendingFork(true);
      }
      setSaveStatus('readonly');
      return;
    }

    setSaveStatus('saving');
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await apiUpdatePlan(activePlan.id, { planData: JSON.parse(snapshotJson) });
        setSaveStatus('saved');
      } catch { setSaveStatus('error'); }
    }, 1500);
    return () => clearTimeout(saveTimer.current);
  }, [snapshotJson]);

  // Latest snapshot, readable from callbacks that were created earlier.
  const latestSnapshotJson = useRef(snapshotJson);
  latestSnapshotJson.current = snapshotJson;

  /* Write pending edits immediately instead of waiting out the debounce.
     Needed by anything that reads the plan back from the server rather than from
     this browser — the PDF export renders whatever is stored, so a change made
     in the last second and a half would silently not appear in the file. */
  const flushSave = useCallback(async () => {
    if (!online || !activePlan) return { saved: false, reason: 'offline' };
    if (!canEditPlan(activePlan)) return { saved: false, reason: 'readonly' };
    clearTimeout(saveTimer.current);
    if (latestSnapshotJson.current === openedJson.current) return { saved: true, reason: 'unchanged' };
    try {
      setSaveStatus('saving');
      await apiUpdatePlan(activePlan.id, { planData: JSON.parse(latestSnapshotJson.current) });
      openedJson.current = latestSnapshotJson.current;
      setSaveStatus('saved');
      return { saved: true };
    } catch {
      setSaveStatus('error');
      return { saved: false, reason: 'error' };
    }
  }, [online, activePlan, canEditPlan]);

  /* Whether the browser holds edits the server does not. Read-only plans always
     do once touched, since nothing will ever persist to them. */
  const hasUnsavedEdits = !!activePlan
    && openedJson.current !== null
    && snapshotJson !== openedJson.current;

  const activeClient = clients.find(c => c.id === activeClientId) || null;
  const advisorPlan = plans.find(p => p.created_by_role === 'advisor') || null;

  // ── Auto-fork on edit ──
  // "My Option 1", "My Option 2", ... counted from the plans this user owns.
  const nextOptionName = useCallback(() => {
    const mine = plans.filter(p => p.created_by_role !== 'advisor').length;
    return `My Option ${mine + 1}`;
  }, [plans]);

  /* Keep the edits: fork the advisor's plan and land the current state in it.
     forkActivePlan already writes the live snapshot into the new plan, so
     nothing the client typed is lost. */
  const confirmFork = useCallback(async (name) => {
    const created = await forkActivePlan((name || '').trim() || nextOptionName());
    if (created) {
      skipNextAutosave.current = false;
      openedJson.current = null;
      setPendingFork(false);
    }
    return created;
  }, [forkActivePlan, nextOptionName]);

  /* Drop the edits and restore the advisor's plan exactly as saved. */
  const discardEdits = useCallback(() => {
    if (!activePlan) return;
    skipNextAutosave.current = true;
    openedJson.current = null;
    loadPlanData(activePlan.plan_data);
    setPendingFork(false);
    setSaveStatus('readonly');
  }, [activePlan, loadPlanData]);

  return {
    isClient, online,
    clients, activeClient, activeClientId, setActiveClientId, refreshClients,
    addClient, editClient, resetClientPassword, removeClient,
    plans, refreshPlans, activePlan, openPlan, saveNewPlan, forkActivePlan,
    renamePlan, removePlan,
    canEditPlan, saveStatus, loading, advisorPlan, flushSave, hasUnsavedEdits,
    pendingFork, confirmFork, discardEdits, nextOptionName,
  };
}
